import { getApiUrl } from "../api";
import { useEffect, useState } from "react";
import Preloader from "../component/Preloader";
import Header from "../component/Header";
import LanguagePopup from "../component/LanguagePopup";
import Footer from "../component/Footer";
import FloatingWidgets from "../component/FloatingWidgets";
import HomeModals from "../component/HomeModals";
import useHomeInteractions from "../component/useHomeInteractions";
import { Link } from "../component/AppLink";
import { ArabicHeader } from "./ArabicHome";

const getJwtToken = () => {
  const keys = ["token", "accessToken", "jwt", "authToken"];
  for (const key of keys) {
    const token = localStorage.getItem(key) || sessionStorage.getItem(key);
    if (token?.trim()) return token.replace(/^Bearer\s+/i, "").trim();
  }

  return (process.env.REACT_APP_JWT_TOKEN || "").replace(/^Bearer\s+/i, "").trim();
};


const stripHtml = (value) => String(value || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

export const createPolicySlug = (title) => (
  stripHtml(title)
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "policy"
);

export const getPolicyDetailsPath = (policy) => (
  `/foundation-policies/${createPolicySlug(policy?.title || policy?.title_en)}/${policy?.id || 0}`
);

const mapPolicy = (item, index, language = "en") => ({
  id: item?.id || String(index + 1),
  title: stripHtml(language === "ar" ? item?.title_ar || item?.title || item?.title_en : item?.title_en || item?.title),
});

const mapPolicyDetails = (item, language = "en") => ({
  id: item?.id || "",
  title: stripHtml(language === "ar" ? item?.title_ar || item?.title || item?.title_en : item?.title_en || item?.title),
  subtitle: stripHtml(language === "ar" ? item?.subtitle_ar || item?.subtitle || item?.subtitle_en || "" : item?.subtitle_en || item?.subtitle || ""),
  description: language === "ar" ? item?.description_ar || item?.description || item?.description_en : item?.description_en || item?.description,
  signature: stripHtml(language === "ar"
    ? item?.nameanddesignation_ar || item?.nameanddesignation || item?.nameanddesignation_en || ""
    : item?.nameanddesignation_en || item?.nameanddesignation || ""),
});

const getPolicyIdFromPath = () => {
  const parts = window.location.pathname.split("/").filter(Boolean);
  const value = parts[parts.length - 1] || "";
  return /^\d+$/.test(value) ? value : "";
};

export default function FoundationPolicyDetails({ language = "en" }) {
  const interactions = useHomeInteractions();
  const [policyId, setPolicyId] = useState(getPolicyIdFromPath);
  const [policy, setPolicy] = useState({});

  useEffect(() => {
    if (language !== "ar") return undefined;
    document.documentElement.lang = "ar";
    document.documentElement.dir = "rtl";
    document.body.classList.add("arabic-layout-page");
    return () => { document.body.classList.remove("arabic-layout-page"); document.documentElement.lang = "en"; document.documentElement.dir = "ltr"; };
  }, [language]);
  const [policies, setPolicies] = useState([]);

  useEffect(() => {
    const updatePolicyId = () => setPolicyId(getPolicyIdFromPath());

    window.addEventListener("popstate", updatePolicyId);
    window.addEventListener("app:navigate", updatePolicyId);

    return () => {
      window.removeEventListener("popstate", updatePolicyId);
      window.removeEventListener("app:navigate", updatePolicyId);
    };
  }, []);

  useEffect(() => {
    document.title = `${policy.title} | Tarahum Charity Foundation`;
  }, [policy.title]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchJson = async (endpoint) => {
      const headers = { Accept: "application/json" };
      const token = getJwtToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(getApiUrl(endpoint), {
        headers,
        credentials: "include",
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`${endpoint} failed with ${response.status}`);
      }

      return response.json();
    };

    const loadPolicyDetails = async () => {
      try {
        const [detailsResult, listResult] = await Promise.all([
          fetchJson(`/api/GetFoundationPoliciesDetails.ashx?id=${encodeURIComponent(policyId)}`),
          fetchJson("/api/GetFoundationPoliciesList.ashx?page=1&pageSize=200"),
        ]);

        const detailsRows = Array.isArray(detailsResult?.data) ? detailsResult.data : [];
        const listRows = Array.isArray(listResult?.data) ? listResult.data : [];

        if (detailsRows[0]) {
          setPolicy(mapPolicyDetails(detailsRows[0], language));
        }

        const mappedPolicies = listRows.map((item, index) => mapPolicy(item, index, language)).filter((item) => item.title);
        if (mappedPolicies.length) {
          setPolicies(mappedPolicies);
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.warn("Unable to load foundation policy details", error);
        }
      }
    };

    loadPolicyDetails();

    return () => controller.abort();
  }, [policyId, language]);

  return (
    <>
      <Preloader />
      {language === "ar" ? <ArabicHeader /> : <Header />}
      {language === "ar" ? null : <LanguagePopup />}

      <section className="kid-d-wrp pb-0">
        <div className="kid-container">
          <div className="kid-hero">
            <h1>{policy.title}</h1>
            <ul className="kid-breadcrumb">
              <li>
                <Link to="/">home</Link>
              </li>
              <li>/</li>
              <li>{policy.title}</li>
            </ul>
          </div>
        </div>
      </section>

      <div className="page-service-single">
        <div className="container">
          <button className="policy-mobile-toggle" type="button" aria-expanded="false" aria-controls="policySidebar">
            <i className="fa-solid fa-bars"></i>
            Policies Menu
          </button>
          <div className="policy-mobile-backdrop"></div>
          <div className="row">
            <div className="col-lg-4">
              <div className="page-single-sidebar policy-mobile-sidebar" id="policySidebar">
                <div className="page-category-list wow fadeInUp">
                 <h3 className="page-category-list-title">{language === "ar" ? "سياسات المؤسسة" : "Foundation's Policies"}</h3>
                    <ul>
                    {policies.map((item) => (
                      <li key={item.id || item.title}>
                        <Link to={getPolicyDetailsPath(item)}>{item.title}</Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div className="col-lg-8">
              <div className="service-single-content">
                <div className="service-entry">
                  {policy.subtitle ? <h3>{policy.subtitle}</h3> : null}
                  <div dangerouslySetInnerHTML={{ __html: policy.description }} />
                  {policy.signature ? (
                    <>
                      <hr />
                      <h6>{policy.signature}</h6>
                    </>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer language={language} />
      <FloatingWidgets {...interactions} />
      <HomeModals {...interactions} />
    </>
  );
}
